import { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ShieldCheck, MapPin, Clock } from "lucide-react";
import { api } from "../lib/api";

const LOGO = "/logo-badge.png";

const DEFAULT_SLIDES = [
  { eyebrow: "EXCAVATION", url: "https://customer-assets.emergentagent.com/job_av-job-command/artifacts/8bkv3r3m_484378192_1085944793336493_7734496098863663277_n.jpg" },
  { eyebrow: "CONCRETE & FOUNDATIONS", url: "https://customer-assets.emergentagent.com/job_av-job-command/artifacts/42b7t9n5_481261813_1077750730822566_2596512597816223226_n.jpg" },
  { eyebrow: "SITE PREP & GRADING", url: "https://customer-assets.emergentagent.com/job_av-job-command/artifacts/rwqn91pt_484133878_1085944853336487_8362770463614297787_n.jpg" },
];

const TRUST = [
  { icon: ShieldCheck, text: "Licensed · Insured · WCB Covered" },
  { icon: MapPin, text: "Vancouver Island & Lower Mainland" },
  { icon: Clock, text: "Free quotes — 48 hour turnaround" },
];

const SERVICE_SUBS = {
  "EXCAVATION": "Site cuts, trenching & land clearing",
  "CONCRETE & FOUNDATIONS": "Footings, walls & flatwork",
  "SITE PREP & GRADING": "Level, compacted & build-ready",
  "DECORATIVE & STAMPED CONCRETE": "Patios, walkways & custom finishes",
  "RETAINING WALLS": "Engineered rock & lock-block",
  "DRIVEWAYS, PAVING & SURFACING": "From gravel to finished grade",
  "DRAINAGE": "Perimeter, Sewage & Sanitary",
};

export default function HeroCarousel() {
  const [slides, setSlides] = useState(DEFAULT_SLIDES);
  const [emblaRef, embla] = useEmblaCarousel(
    { loop: true, duration: 30 },
    [Autoplay({ delay: 5500, stopOnInteraction: false, stopOnMouseEnter: true })]
  );
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    api.get("/hero")
      .then((r) => {
        if (r.data?.length) setSlides(r.data.map((s) => ({ eyebrow: s.heading.toUpperCase(), url: s.image_url })));
      })
      .catch(() => {});
  }, []);

  const onSelect = useCallback(() => {
    if (!embla) return;
    setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    embla.reInit();
    embla.on("select", onSelect);
    onSelect();
    return () => embla.off("select", onSelect);
  }, [embla, onSelect, slides]);

  const current = slides[selected] || slides[0];

  return (
    <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-[#1A1F22]" data-testid="hero-carousel">
      {/* Each heading rotates WITH its assigned photo */}
      <div className="embla h-full absolute inset-0" ref={emblaRef}>
        <div className="embla__container h-full">
          {slides.map((s, idx) => (
            <div className="embla__slide h-full" key={s.eyebrow || idx} aria-hidden={selected !== idx}>
              <img src={s.url} alt={s.eyebrow} className="absolute inset-0 w-full h-full object-cover" loading={idx === 0 ? "eager" : "lazy"} fetchPriority={idx === 0 ? "high" : "auto"} />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1A1F22]/85 via-[#1A1F22]/30 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1F22]/80 via-transparent to-[#1A1F22]/50" />
            </div>
          ))}
        </div>
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10 lg:px-16 h-full flex flex-col justify-between pt-24 md:pt-32 pb-20 md:pb-24 lg:pb-28">
        {/* Large brand badge — front and center on landing */}
        <img
          src={LOGO}
          alt="Amazing View Contracting — Amazing is What We Thrive For"
          data-testid="hero-logo"
          className="self-start w-[12.5rem] sm:w-60 md:w-[18.75rem] lg:w-[22.5rem] h-auto av-rise drop-shadow-[0_12px_35px_rgba(0,0,0,0.65)] pointer-events-none select-none"
        />
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <div className="lg:col-span-8">
            <div className="text-[#9CC4DB] text-xs md:text-sm tracking-[0.35em] font-bold uppercase mb-4 drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]" data-testid="hero-brand-line">
              Amazing is What We Thrive For <span className="text-white/40">·</span> Vancouver Island <span className="text-white/40">·</span> Lower Mainland
            </div>
            <div key={current.eyebrow}>
              <h1 className="font-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] xl:text-[6rem] uppercase leading-[0.92] av-rise drop-shadow-[0_4px_20px_rgba(0,0,0,0.55)]" data-testid="hero-title">
                {current.eyebrow}.
              </h1>
              <p className="mt-5 font-display text-[#9CC4DB] text-2xl sm:text-3xl uppercase tracking-wide av-rise drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]" data-testid={`hero-eyebrow-${selected}`}>
                {SERVICE_SUBS[(current.eyebrow || "").toUpperCase()] || "Excavation, concrete & site work across British Columbia"}
              </p>
            </div>
            <div className="mt-8 flex flex-wrap gap-3 av-rise">
              <a href="/contact" data-testid="hero-cta-quote" className="bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm md:text-base font-bold tracking-[0.15em] px-8 py-4 uppercase inline-flex items-center gap-3 shadow-[0_10px_30px_rgba(47,95,128,0.45)] transition-colors">
                Request a Quote
                <span className="block w-6 h-px bg-white" />
              </a>
              <a href="/past-jobs" data-testid="hero-cta-jobs" className="border-2 border-white/70 hover:border-[#9CC4DB] hover:text-[#9CC4DB] text-white text-sm md:text-base font-bold tracking-[0.15em] px-8 py-4 uppercase transition-colors">
                See Past Jobs
              </a>
            </div>
          </div>

          <div className="lg:col-span-4 hidden lg:flex justify-end av-rise" data-testid="hero-trust-panel">
            <TrustPanel />
          </div>
        </div>
      </div>

      <HeroIndicators slides={slides} selected={selected} onGoTo={(i) => embla && embla.scrollTo(i)} />
    </section>
  );
}

function TrustPanel() {
  return (
    <div className="w-full max-w-[340px] bg-[#1A1F22]/70 backdrop-blur-md border-l-4 border-[#9CC4DB] shadow-[0_20px_50px_rgba(0,0,0,0.4)] px-6 py-6 space-y-4">
      {TRUST.map(({ icon: Icon, text }) => (
        <div key={text} className="flex items-center gap-3 text-white/90 text-sm tracking-wide">
          <Icon size={18} className="text-[#9CC4DB] shrink-0" />
          {text}
        </div>
      ))}
    </div>
  );
}

function HeroIndicators({ slides, selected, onGoTo }) {
  return (
    <div className="absolute bottom-6 md:bottom-8 left-5 md:left-10 lg:left-16 z-20 flex items-center gap-2.5" data-testid="hero-indicators">
      {slides.map((s, i) => (
        <button
          key={s.eyebrow || i}
          onClick={() => onGoTo(i)}
          data-testid={`hero-indicator-${i}`}
          aria-label={`Slide ${i + 1}`}
          className={`h-1.5 transition-all ${selected === i ? "w-12 bg-[#9CC4DB]" : "w-6 bg-white/40 hover:bg-white"}`}
        />
      ))}
    </div>
  );
}
